import React from "react";
import clsx from "clsx";

import type {
    GridProps,
    GetRowIdFunc,
    ColDef,
    GridReadyEvent,
    IHeaderParams,
    IHeaderGroupParams,
} from "@/ui/molecules/Grid";
import Grid, { GridLoaderSkeleton } from "@/ui/molecules/Grid";
import GridHeaderComponent from "@/ui/molecules/GridHeaderComponent";

import Box from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";
import Badge from "@/ui/atoms/Badge";

export type TChartOfAccountsRow = {
    type: "income" | "expense" | "non-operating";
    code: string;
    account: string;
    section: string | null;
    subcategory: string | null;
};

export type GridChartOfAccountsProps = Omit<
    GridProps<TChartOfAccountsRow>,
    "columnDefs" | "rowData" | "getRowStyle"
> & {
    title?: string;
    rowData: TChartOfAccountsRow[];
    onGridReady?: (params: GridReadyEvent) => void;
};

export const testIds = {
    main: "GridChartOfAccounts",
    header: "GridChartOfAccounts-header",
    badge: "GridChartOfAccounts-badge",
    titleBar: "GridChartOfAccounts-titleBar",
};

const TitleHeaderComponent = (props: IHeaderGroupParams) => {
    const { displayName } = props;

    return (
        <Box
            data-testid={testIds.titleBar}
            sx={{
                backgroundColor: "var(--primary-color)",
                color: "var(--proda-white)",
                padding: "12px 16px",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
            }}
        >
            <Typography
                variant="h3"
                sx={{
                    color: "var(--proda-white)",
                }}
            >
                {displayName}
            </Typography>
        </Box>
    );
};

const CellRenderer = (params: { value: string }) => {
    const value = params.value;

    return <Typography variant="body2">{value}</Typography>;
};

const HeaderComponent = (params: IHeaderParams) => (
    <GridHeaderComponent {...params} textAlign="left" />
);

const GridChartOfAccounts = React.memo((props: GridChartOfAccountsProps) => {
    const {
        rowData,
        title = "Chart of Accounts",
        className,
        onGridReady,
        ...otherProps
    } = props;

    const getRowId: GetRowIdFunc<TChartOfAccountsRow> = React.useCallback(
        params => String(params.data.code),
        [],
    );

    const TypeCellRenderer = React.useCallback((params: { value: string }) => {
        const value = params.value as "income" | "expense" | "non-operating";

        const severity =
            value === "income"
                ? "success"
                : value === "expense"
                  ? "error"
                  : "inactive";

        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    paddingLeft: "16px",
                }}
            >
                <Badge data-testid={testIds.badge} severity={severity}>
                    <Typography
                        variant="body4"
                        sx={{
                            textTransform: "capitalize",
                        }}
                    >
                        {value}
                    </Typography>
                </Badge>
            </Box>
        );
    }, []);

    const columnDefs = React.useMemo<ColDef<TChartOfAccountsRow>[]>(
        () => [
            {
                headerName: title,
                headerGroupComponent: TitleHeaderComponent,
                children: [
                    {
                        field: "type",
                        headerName: "Type",
                        headerComponent: HeaderComponent,
                        width: 150,
                        cellRenderer: TypeCellRenderer,
                        sortable: true,
                    },
                    {
                        field: "code",
                        headerName: "Code",
                        headerComponent: HeaderComponent,
                        width: 150,
                        cellRenderer: CellRenderer,
                    },
                    {
                        field: "account",
                        headerName: "Account",
                        headerComponent: HeaderComponent,
                        flex: 1,
                        minWidth: 200,
                        cellRenderer: CellRenderer,
                    },
                    {
                        field: "section",
                        headerName: "Section / Total",
                        headerComponent: HeaderComponent,
                        flex: 1,
                        minWidth: 200,
                        cellRenderer: CellRenderer,
                    },
                    {
                        field: "subcategory",
                        headerName: "Sub-Category",
                        headerComponent: HeaderComponent,
                        flex: 1,
                        minWidth: 200,
                        cellRenderer: CellRenderer,
                    },
                    {
                        field: "displayOrder",
                        sort: "asc",
                        sortIndex: 0,
                        hide: true,
                    },
                ],
            },
        ],
        [TypeCellRenderer, title],
    );

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
            }}
        >
            <Grid<TChartOfAccountsRow>
                data-testid={testIds.main}
                className={clsx([testIds.main, className])}
                gridTheme="ag-theme-custom-proda"
                rowData={rowData}
                columnDefs={columnDefs}
                getRowId={getRowId}
                defaultColDef={{
                    suppressMovable: true,
                    sortable: false,
                    resizable: true,
                }}
                getRowStyle={() => ({
                    backgroundColor: "var(--proda-white)",
                })}
                suppressHeaderFocus={true}
                enableBrowserTooltips={true}
                onGridReady={onGridReady}
                headerHeight={48}
                groupHeaderHeight={48}
                gridLoaderComponent={GridLoaderSkeleton}
                suppressRowVirtualisation={true}
                suppressColumnVirtualisation={true}
                {...otherProps}
            />
        </Box>
    );
});

GridChartOfAccounts.displayName = "GridChartOfAccounts";

export default GridChartOfAccounts;
